/** Minimal typed HTTP helper shared by the console and agent clients. Works in browsers, Node 22+ and Workers. */
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message)
  }
}

export interface HttpOptions {
  baseUrl: string
  /** Returns the bearer token for each request (Supabase access token or agent token). */
  token?: () => string | null | undefined | Promise<string | null | undefined>
  fetch?: typeof fetch
  headers?: Record<string, string>
}

export class Http {
  constructor(private opts: HttpOptions) {}

  async request<T>(method: string, path: string, body?: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
    const token = await this.opts.token?.()
    const headers: Record<string, string> = { accept: 'application/json', ...this.opts.headers, ...extraHeaders }
    if (token) headers.authorization = `Bearer ${token}`
    let payload: string | undefined
    if (body !== undefined) {
      headers['content-type'] = 'application/json'
      payload = typeof body === 'string' ? body : JSON.stringify(body)
    }
    const f = this.opts.fetch ?? fetch
    const res = await f(this.opts.baseUrl.replace(/\/$/, '') + path, { method, headers, body: payload })
    const text = await res.text()
    const json = text ? safeJson(text) : null
    if (!res.ok) {
      const e = (json as any)?.error
      throw new ApiError(res.status, e?.code ?? `http_${res.status}`, e?.message ?? (text.slice(0, 300) || res.statusText), e?.details)
    }
    return json as T
  }

  /** The raw response (binary bodies such as recordings); throws ApiError like request(). */
  async raw(method: string, path: string): Promise<Response> {
    const token = await this.opts.token?.()
    const headers: Record<string, string> = { ...this.opts.headers }
    if (token) headers.authorization = `Bearer ${token}`
    const f = this.opts.fetch ?? fetch
    const res = await f(this.opts.baseUrl.replace(/\/$/, '') + path, { method, headers })
    if (!res.ok) {
      const text = await res.text()
      const e = (safeJson(text) as any)?.error
      throw new ApiError(res.status, e?.code ?? `http_${res.status}`, e?.message ?? (text.slice(0, 300) || res.statusText), e?.details)
    }
    return res
  }

  /** A binary response (a file), with the same auth and error handling as JSON requests. */
  async blob(path: string): Promise<Blob> {
    return (await this.raw('GET', path)).blob()
  }

  get<T>(path: string, query?: Record<string, string | number | boolean | undefined | null>) {
    return this.request<T>('GET', path + qs(query))
  }
  post<T>(path: string, body?: unknown, headers?: Record<string, string>) { return this.request<T>('POST', path, body ?? {}, headers) }
  patch<T>(path: string, body: unknown, headers?: Record<string, string>) { return this.request<T>('PATCH', path, body, headers) }
  put<T>(path: string, body: unknown) { return this.request<T>('PUT', path, body) }
  delete<T>(path: string, headers?: Record<string, string>) { return this.request<T>('DELETE', path, undefined, headers) }
}

export function qs(query?: Record<string, string | number | boolean | undefined | null>) {
  if (!query) return ''
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  const s = p.toString()
  return s ? `?${s}` : ''
}

function safeJson(t: string) {
  try { return JSON.parse(t) } catch { return { raw: t } }
}
